import { zodResolver } from "@hookform/resolvers/zod";
import UserValidator from "Validators/Common/User";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import * as z from "zod";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { getPasswordSecurityVerifications } from "Helpers/Password";
import { clearMask } from "Helpers/Util";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import SafePassword from "Components/Admin/SafePassword";
import Section from "Components/Admin/Section";
import ToggleStatus from "Components/Admin/ToggleStatus";

import * as S from "./styles";

const UpdateSponsor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [selectedUser, setSelectedUser] = useState({});
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (id) {
      return getUser(id);
    }

    // eslint-disable-next-line
  }, [id]);

  const formSchema = z.object({
    full_name: UserValidator.full_name,
    email: UserValidator.email,
    phone: UserValidator.phone,
    role: UserValidator.role,
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
  });
  const { register, handleSubmit, setValue } = methods;

  const getUser = (id) => {
    api
      .post("/users/get-user-by-id", { user_id: id })
      .then((response) => {
        const user = response.data;
        setSelectedUser(user);

        setValue("full_name", user.full_name);
        setValue("email", user.email);
        // ⚠️ `|| ""` obrigatório: `phone` é MASCARADO e o react-input-mask faz
        // `value.split("")` no componentDidUpdate — com nulo, estoura
        // "Cannot read properties of null" e a tela fica BRANCA, sem mensagem.
        setValue("phone", user.phone || "");
        setValue("role", user.role || "");
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  // Valida força e confirmação da senha. Retorna false (e notifica) se inválida.
  const isPasswordValid = () => {
    const verifications = getPasswordSecurityVerifications(
      password,
      null,
      false,
    );
    if (
      !verifications.hasEnoughLength ||
      !verifications.hasNumber ||
      !verifications.hasUppercase ||
      !verifications.hasSpecialCharacters
    ) {
      showNotification(
        "danger",
        "Atenção!",
        "A senha não atende aos requisitos mínimos de segurança!",
      );
      return false;
    }

    if (password !== confirmPassword) {
      showNotification("danger", "Atenção!", "As senhas não conferem!");
      return false;
    }

    return true;
  };

  const submit = (data) => {
    // Senha é opcional: só valida/envia se algo foi preenchido.
    if (password || confirmPassword) {
      if (!isPasswordValid()) return;
      data.password = password;
    }

    setLoading(true);

    if (data.phone) data.phone = clearMask(data.phone);

    api
      .post("/sponsors/update-user", {
        user_id: selectedUser.id,
        user: data,
      })
      .then(() => {
        setLoading(false);

        showNotification(
          "success",
          "Sucesso!",
          "Usuário patrocinador atualizado com sucesso!",
        );
      })
      .catch((error) => {
        setLoading(false);
        return HTTPErrorCallback(error);
      });
  };

  return (
    <Section>
      <S.Wrapper>
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            disabled={loading}
            onClick={() => navigate(AdminRoutes.ListSponsors.path)}
            width="30%"
          />
          <ToggleStatus
            route={"/sponsors/toggle-user-status/"}
            item={selectedUser}
            refresh={() => getUser(id)}
          />
        </S.Buttons>
        <FormWrapper submit={handleSubmit(submit)} {...methods}>
          <Input
            {...register("full_name")}
            label="Nome:"
            placeholder="Nome"
            type="text"
            required
          />
          <Input
            {...register("email")}
            type="email"
            label="E-mail"
            placeholder="E-mail"
            required
            disabled
          />
          <Input
            {...register("phone")}
            type="text"
            label="Telefone:"
            placeholder="Telefone"
            required
            mask="+99 (99) 99999-9999"
          />
          <Input
            {...register("role")}
            type="text"
            label="Cargo:"
            placeholder="Cargo"
            required
          />

          <Input
            name="password"
            type="password"
            label="Criar senha:"
            placeholder="Deixe em branco para não alterar"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            name="confirm_password"
            type="password"
            label="Confirmar nova senha:"
            placeholder="Confirmar senha"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <SafePassword password={password} />

          <Button type="submit" text="Atualizar" disabled={loading} />
        </FormWrapper>
      </S.Wrapper>
    </Section>
  );
};

export default UpdateSponsor;
